<template>
  <transition name="celebration-fade">
    <div v-if="visible" class="celebration-overlay" role="dialog" aria-modal="true">
      <!-- Dark blur scrim — z-1005 -->
      <div class="celebration-scrim"></div>

      <!-- Confetti canvases — blurred bg (z-1009) + sharp fg (z-1011) -->
      <canvas ref="confettiBg" class="celebration-canvas-bg"></canvas>
      <canvas ref="confettiFg" class="celebration-canvas-fg"></canvas>

      <!-- Centered modal card — z-1020. Clicking the backdrop (not the card) closes. -->
      <div class="celebration-modal-wrap" @click.self="handleClose">
        <div class="celebration-modal-card" :style="cardStyle">
          <slot :close="handleClose" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { defineComponent } from 'vue';
import { createConfetti } from './confetti';

/**
 * Reusable celebration overlay: full-screen ASU confetti + a centered modal
 * card whose contents are provided via the default slot. The slot receives a
 * `close` function so inner controls (e.g. an X button) can dismiss it.
 *
 * Dismisses on: backdrop click, or any inner control that calls `close`.
 * Owns the confetti lifecycle — shower runs while visible, stops on close.
 *
 *   <CelebrationOverlay :visible="show" @close="show = false">
 *     <template #default="{ close }"> ...modal content... </template>
 *   </CelebrationOverlay>
 */
export default defineComponent({
  name: 'CelebrationOverlay',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** Max width of the centered card in px (Figma default: 588). */
    maxWidth: {
      type: Number,
      default: 588
    }
  },

  emits: ['close'],

  data() {
    return {
      confetti: null
    };
  },

  computed: {
    cardStyle() {
      return { width: `min(${this.maxWidth}px, 92vw)` };
    }
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => this.startConfetti());
      } else {
        this.stopConfetti();
      }
    }
  },

  mounted() {
    if (this.visible) {
      this.$nextTick(() => this.startConfetti());
    }
  },

  beforeUnmount() {
    if (this.confetti) {
      this.confetti.destroy();
      this.confetti = null;
    }
  },

  methods: {
    handleClose() {
      this.stopConfetti();
      this.$emit('close');
    },

    startConfetti() {
      const bg = this.$refs.confettiBg;
      const fg = this.$refs.confettiFg;
      if (!bg || !fg) return;
      // Canvases live under v-if, so each open remounts fresh elements — rebuild
      // the controller against the current refs (old one is detached).
      if (this.confetti) this.confetti.destroy();
      this.confetti = createConfetti(bg, fg);
      this.confetti.start();
    },

    stopConfetti() {
      if (this.confetti) this.confetti.stop();
    }
  }
});
</script>

<style scoped>
/* ── Layer order (mirrors asu-celebration-overlay.html) ──────────────────────
   z-1005  dark blur scrim
   z-1009  blurred confetti bg canvas
   z-1011  sharp confetti fg canvas
   z-1020  modal card
─────────────────────────────────────────────────────────────────────────── */
.celebration-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

.celebration-scrim {
  position: fixed;
  inset: 0;
  z-index: 1005;
  background: rgba(20, 6, 12, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.celebration-canvas-bg,
.celebration-canvas-fg {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.celebration-canvas-bg {
  z-index: 1009;
  filter: blur(3px);
  opacity: 0.85;
}
.celebration-canvas-fg {
  z-index: 1011;
}

.celebration-modal-wrap {
  position: fixed;
  inset: 0;
  z-index: 1020;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

/* White card — Figma node 162:10356: 12px radius, 24px padding */
.celebration-modal-card {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.35);
  animation: celebrationPop 0.5s cubic-bezier(0.18, 0.89, 0.32, 1.28);
}
@keyframes celebrationPop {
  0% {
    transform: scale(0.7) translateY(20px);
    opacity: 0;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.celebration-fade-enter-active,
.celebration-fade-leave-active {
  transition: opacity 0.35s ease;
}
.celebration-fade-enter-from,
.celebration-fade-leave-to {
  opacity: 0;
}
</style>
